import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, ZapIcon, ShieldIcon, StarIcon, XIcon, GiftIcon, CheckIcon } from 'lucide-react';
type PlanId = 'base' | 'pro' | 'biblioteca';
interface PlanDetail {
  icon: string;
  text: string;
  isHighlight?: boolean;
}
interface Plan {
  id: PlanId;
  name: string;
  price: string;
  originalPrice: string;
  summary: string;
  link: string;
  savings?: string;
  details: PlanDetail[];
}
const plans: Plan[] = [{
  id: 'base',
  name: 'Plan Base',
  price: '18.990',
  originalPrice: '49.990',
  summary: '4 Módulos + 1 Bono',
  link: 'https://checkout.empezahoy.shop/cart/48129330675944:1',
  details: [{
    icon: '✅',
    text: 'Módulo 1 – Informes terapéuticos profesionales'
  }, {
    icon: '✅',
    text: 'Módulo 2 – Evaluaciones terapéuticas'
  }, {
    icon: '✅',
    text: 'Módulo 3 – Registro de sesiones terapéuticas'
  }, {
    icon: '✅',
    text: 'Módulo 4 – Banco de actividades terapéuticas'
  }, {
    icon: '✅',
    text: 'Bono 1 – Banco de Actividades de Motricidad Fina'
  }]
}, {
  id: 'pro',
  name: 'Plan PRO',
  price: '23.990',
  originalPrice: '69.990',
  summary: '5 Módulos + 3 Bonos completos',
  link: 'https://checkout.empezahoy.shop/cart/48129316815080:1',
  savings: 'AHORRÁS $15.000',
  details: [{
    icon: '✨',
    text: 'Todo lo del Plan Base +',
    isHighlight: true
  }, {
    icon: '✅',
    text: 'Módulo 5 – Seguimiento del progreso del paciente'
  }, {
    icon: '✅',
    text: 'Bono 2 – Kit Evaluación de Motricidad Fina'
  }, {
    icon: '✅',
    text: 'Bono 3 – Banco de Actividades de Estimulación Cognitiva'
  }, {
    icon: '✅',
    text: 'Acceso de por vida + actualizaciones gratuitas'
  }]
}, {
  id: 'biblioteca',
  name: 'Plan Biblioteca Completa',
  price: '29.990',
  originalPrice: '89.990',
  summary: 'Plan PRO + 3 Bibliotecas terapéuticas',
  link: 'https://checkout.empezahoy.shop/cart/48129333330152:1',
  details: [{
    icon: '✨',
    text: 'Todo lo del Plan PRO +',
    isHighlight: true
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas Infantil – Motricidad fina, coordinación y estimulación cognitiva con niños'
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas para Adolescentes – Habilidades funcionales, coordinación y estimulación cognitiva'
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas para Adultos – Rehabilitación funcional, autonomía y estimulación cognitiva'
  }, {
    icon: '💡',
    text: 'Biblioteca completa de recursos para pacientes de distintas edades'
  }]
}];
export function PricingSection() {
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>('pro');
  const [showPopup, setShowPopup] = useState(false);
  const [upgradePro, setUpgradePro] = useState(false);
  const [upgradeBiblioteca, setUpgradeBiblioteca] = useState(false);
  const selectedPlan = plans.find(p => p.id === selectedPlanId)!;
  const handleCtaClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (selectedPlanId === 'biblioteca') {
      return;
    }
    e.preventDefault();
    setUpgradePro(false);
    setUpgradeBiblioteca(false);
    setShowPopup(true);
  };
  const getCheckoutUrl = () => {
    if (selectedPlanId === 'base') {
      if (upgradePro && upgradeBiblioteca) {
        return 'https://checkout.empezahoy.shop/cart/48129333330152:1';
      } else if (upgradePro) {
        return 'https://checkout.empezahoy.shop/cart/48129316815080:1';
      } else if (upgradeBiblioteca) {
        return 'https://checkout.empezahoy.shop/cart/48156773744872:1';
      }
      return 'https://checkout.empezahoy.shop/cart/48129330675944:1';
    } else if (selectedPlanId === 'pro') {
      if (upgradeBiblioteca) {
        return 'https://checkout.empezahoy.shop/cart/48129333330152:1';
      }
      return 'https://checkout.empezahoy.shop/cart/48129316815080:1';
    }
    return selectedPlan.link;
  };
  const hasUpgrades = upgradePro || upgradeBiblioteca;
  const getTotal = () => {
    const basePrice = selectedPlanId === 'base' ? 18990 : 23990;
    let total = basePrice;
    if (selectedPlanId === 'base' && upgradePro) total += 5000;
    if (upgradeBiblioteca) total += 6000;
    return total.toLocaleString('es-AR');
  };
  return <section id="comprar" className="py-16 md:py-24 px-4 bg-cream relative" data-id="element-94">
      <div className="max-w-3xl mx-auto" data-id="element-95">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-96">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-olive-dark mb-4" data-id="element-97">
            Elegí tu plan
          </h2>
          <p className="text-lg text-olive/80" data-id="element-98">
            Acceso inmediato y de por vida. Pago único, sin suscripciones.
          </p>
        </motion.div>

        {/* Plan Cards */}
        <div className="space-y-4 mb-8" data-id="element-99">
          {plans.map(plan => {
          const isSelected = selectedPlanId === plan.id;
          return <motion.div key={plan.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} onClick={() => setSelectedPlanId(plan.id)} className={`relative cursor-pointer rounded-2xl p-5 md:p-6 transition-all duration-300 border-2 flex items-center gap-4 md:gap-6 ${isSelected ? 'border-golden bg-golden/5 shadow-md' : 'border-olive/10 bg-white hover:border-golden/40 shadow-sm'}`} data-id="element-100">
                {/* Radio Circle */}
                <div className="shrink-0 flex items-center justify-center" data-id="element-101">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-golden' : 'border-olive/30'}`} data-id="element-102">
                    {isSelected && <motion.div layoutId="radio-fill" className="w-3 h-3 bg-golden rounded-full" data-id="element-103" />}
                  </div>
                </div>

                {/* Plan Info */}
                <div className="flex-1 min-w-0" data-id="element-104">
                  {plan.id === 'pro' && <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-105">
                      <span className="inline-flex items-center gap-1 bg-golden text-white text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-106">
                        <StarIcon className="w-3 h-3 fill-white" data-id="element-107" />
                        Más Elegido
                      </span>
                      {plan.savings && <span className="bg-olive/10 text-olive-dark text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-108">
                          {plan.savings}
                        </span>}
                    </div>}
                  <h3 className="text-xl md:text-2xl font-serif font-bold text-olive-dark leading-tight" data-id="element-109">
                    {plan.name}
                  </h3>
                  <p className="text-sm md:text-base text-olive/70 mt-1" data-id="element-110">
                    {plan.summary}
                  </p>
                </div>

                {/* Price */}
                <div className="text-right shrink-0" data-id="element-111">
                  <div className="text-sm md:text-base text-olive/50 line-through font-medium mb-0.5" data-id="element-112">
                    ${plan.originalPrice}
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-golden tracking-tight" data-id="element-113">
                    ${plan.price}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-8" data-id="element-114">
          <a href={selectedPlan.link} onClick={handleCtaClick} className="block w-full text-center bg-golden hover:bg-golden-hover text-white py-5 md:py-6 rounded-full text-lg md:text-2xl font-bold uppercase tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-115">
            {selectedPlanId === 'base' && '👉quiero empezar con el plan básico'}
            {selectedPlanId === 'pro' && '👉SI, Quiero el sistema PRO'}
            {selectedPlanId === 'biblioteca' && '👉Si, Quiero la biblioteca completa'}
          </a>
        </motion.div>

        {/* Animated Details Section */}
        <AnimatePresence mode="wait" data-id="element-116">
          <motion.div key={selectedPlan.id} initial={{
          opacity: 0,
          height: 0,
          y: -10
        }} animate={{
          opacity: 1,
          height: 'auto',
          y: 0
        }} exit={{
          opacity: 0,
          height: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden mb-8" data-id="element-117">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-olive/10 shadow-sm" data-id="element-118">
              <h4 className="font-bold text-olive-dark mb-5 text-lg border-b border-olive/10 pb-3" data-id="element-119">
                ¿Qué incluye el {selectedPlan.name}?
              </h4>
              <ul className="space-y-4" data-id="element-120">
                {selectedPlan.details.map((detail, index) => <li key={index} className="flex items-start gap-3" data-id="element-121">
                    <span className="shrink-0 mt-0.5 text-lg" data-id="element-122">
                      {detail.icon}
                    </span>
                    <span className={`text-base leading-relaxed ${detail.isHighlight ? 'font-bold text-olive-dark' : 'text-olive/80'}`} data-id="element-123">
                      {detail.text}
                    </span>
                  </li>)}
              </ul>

              {selectedPlan.id === 'pro' && <p className="text-center italic text-olive/60 mt-8 text-sm font-medium" data-id="element-124">
                  La opción más elegida por terapeutas ocupacionales
                </p>}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm font-medium text-olive/70 bg-olive/5 py-4 px-6 rounded-2xl border border-olive/10" data-id="element-125">
          <div className="flex items-center gap-2" data-id="element-126">
            <LockIcon className="w-4 h-4 text-golden" data-id="element-127" />
            <span data-id="element-128">Pago seguro</span>
          </div>
          <div className="hidden md:block w-1 h-1 bg-olive/20 rounded-full" data-id="element-129" />
          <div className="flex items-center gap-2" data-id="element-130">
            <ZapIcon className="w-4 h-4 text-golden" data-id="element-131" />
            <span data-id="element-132">Acceso inmediato</span>
          </div>
          <div className="hidden md:block w-1 h-1 bg-olive/20 rounded-full" data-id="element-133" />
          <div className="flex items-center gap-2" data-id="element-134">
            <ShieldIcon className="w-4 h-4 text-golden" data-id="element-135" />
            <span data-id="element-136">Garantía 7 días</span>
          </div>
        </motion.div>
      </div>

      {/* Upsell Modal */}
      <AnimatePresence data-id="element-137">
        {showPopup && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-138">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowPopup(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-139" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden z-10 max-h-[90vh] overflow-y-auto" data-id="element-140">
              <button onClick={() => setShowPopup(false)} className="absolute top-4 right-4 p-2 text-olive/40 hover:text-olive-dark hover:bg-olive/5 rounded-full transition-colors z-10" aria-label="Cerrar" data-id="element-141">
                <XIcon className="w-5 h-5" data-id="element-142" />
              </button>

              <div className="p-5 md:p-6" data-id="element-143">
                {/* Gift Icon + Header */}
                <div className="flex items-center gap-3 mb-1" data-id="element-144">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-145">
                    <GiftIcon className="w-5 h-5 text-amber-500" data-id="element-146" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-olive-dark leading-tight" data-id="element-147">
                    ¡Mejorá tu compra!
                  </h3>
                </div>
                <p className="text-olive/60 text-xs mb-4 ml-[52px]" data-id="element-148">
                  Aprovechá estas ofertas exclusivas
                </p>

                {/* Upsell Options */}
                <div className="space-y-3 mb-4" data-id="element-149">
                  {selectedPlanId === 'base' && <div onClick={() => setUpgradePro(!upgradePro)} className={`cursor-pointer rounded-lg p-3 transition-all duration-200 border-2 flex items-start gap-2.5 ${upgradePro ? 'border-emerald-400 bg-emerald-50' : 'border-olive/10 bg-white hover:border-olive/20'}`} data-id="element-150">
                      <div className="shrink-0 mt-0.5" data-id="element-151">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${upgradePro ? 'border-emerald-500 bg-emerald-500' : 'border-olive/25 bg-white'}`} data-id="element-152">
                          {upgradePro && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-153" />}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-154">
                        <p className="font-bold text-olive-dark text-sm leading-tight" data-id="element-155">
                          ⭐ Llevate el Sistema PRO Completo
                        </p>
                        <p className="text-olive/55 text-xs leading-snug mt-0.5" data-id="element-156">
                          Módulo 5 + Kit Evaluación Motricidad Fina + Banco
                          Estimulación Cognitiva
                        </p>
                        <p className="text-amber-500 font-bold text-sm mt-1" data-id="element-157">
                          + $5.000{' '}
                          <span className="text-olive/40 font-normal text-xs" data-id="element-158">
                            ARS
                          </span>
                        </p>
                      </div>
                    </div>}

                  <div onClick={() => setUpgradeBiblioteca(!upgradeBiblioteca)} className={`cursor-pointer rounded-lg p-3 transition-all duration-200 border-2 flex items-start gap-2.5 ${upgradeBiblioteca ? 'border-amber-400 bg-amber-50' : 'border-olive/10 bg-white hover:border-olive/20'}`} data-id="element-159">
                    <div className="shrink-0 mt-0.5" data-id="element-160">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${upgradeBiblioteca ? 'border-amber-500 bg-amber-500' : 'border-olive/25 bg-white'}`} data-id="element-161">
                        {upgradeBiblioteca && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-162" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-163">
                      <p className="font-bold text-olive-dark text-sm leading-tight" data-id="element-164">
                        📚 3 Bibliotecas Terapéuticas Completas
                      </p>
                      <p className="text-olive/55 text-xs leading-snug mt-0.5" data-id="element-165">
                        Actividades para pacientes infantiles, adolescentes y
                        adultos.
                      </p>
                      <p className="text-amber-500 font-bold text-sm mt-1" data-id="element-166">
                        + $6.000{' '}
                        <span className="text-olive/40 font-normal text-xs" data-id="element-167">
                          ARS
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-olive/5" data-id="element-168">
                  <div className="space-y-1.5" data-id="element-169">
                    <div className="flex justify-between items-center" data-id="element-170">
                      <span className="text-olive/80 text-xs" data-id="element-171">
                        {selectedPlanId === 'base' ? 'Plan Base' : 'Plan PRO'}
                      </span>
                      <span className="text-olive-dark font-semibold text-xs" data-id="element-172">
                        ${selectedPlanId === 'base' ? '18.990' : '23.990'}
                      </span>
                    </div>
                    {selectedPlanId === 'base' && upgradePro && <div className="flex justify-between items-center" data-id="element-173">
                        <span className="text-golden text-xs" data-id="element-174">
                          Upgrade Sistema PRO
                        </span>
                        <span className="text-golden font-semibold text-xs" data-id="element-175">
                          + $5.000
                        </span>
                      </div>}
                    {upgradeBiblioteca && <div className="flex justify-between items-center" data-id="element-176">
                        <span className="text-golden text-xs" data-id="element-177">
                          Bibliotecas Terapéuticas
                        </span>
                        <span className="text-golden font-semibold text-xs" data-id="element-178">
                          + $6.000
                        </span>
                      </div>}
                    <div className="border-t border-olive/10 pt-1.5 mt-1.5" data-id="element-179">
                      <div className="flex justify-between items-center" data-id="element-180">
                        <span className="text-olive-dark font-bold text-sm" data-id="element-181">
                          Total
                        </span>
                        <span className="text-golden font-bold text-lg" data-id="element-182">
                          ${getTotal()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <a href={getCheckoutUrl()} className="block w-full text-center bg-olive-dark hover:bg-olive text-white py-3.5 rounded-xl text-sm font-bold transition-all shadow-md hover:shadow-lg" data-id="element-183">
                  {hasUpgrades ? '👉 Comprar con mejoras' : `👉 Continuar con el ${selectedPlanId === 'base' ? 'Plan Base' : 'Plan PRO'}`}
                </a>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </section>;
}